import { ReactNode, useEffect, useState } from "react";
import { Ico } from "./Icons";
import * as ContextMenu from "@radix-ui/react-context-menu";

export type TreeContextAction = {
    label: string;
    onSelect: () => void;
    disabled?: boolean;
    danger?: boolean;
};

interface TreeSectionProps {
    label: string;
    icon: ReactNode;
    defaultOpen?: boolean;
    selectedId: string;
    onSelect: (id: string) => void;
    nodeId: string;
    children: ReactNode;
    forceOpen?: boolean;
    contextActions?: TreeContextAction[];
}

export default function TreeSection({
    label,
    icon,
    defaultOpen = true,
    selectedId,
    onSelect,
    nodeId,
    children,
    forceOpen = false,
    contextActions,
}: TreeSectionProps) {
    const [open, setOpen] = useState(defaultOpen);
    const isSelected = selectedId === nodeId;
    const hasContextActions = Boolean(contextActions && contextActions.length > 0);

    useEffect(() => {
        if (forceOpen) setOpen(true);
    }, [forceOpen]);

    const headerButton = (
        <button
            type="button"
            className={`tree-node tree-section-node${isSelected ? " tree-node--selected" : ""}`}
            style={{ paddingLeft: 8 }}
            onClick={() => {
                if (isSelected) {
                    setOpen((p) => !p);
                } else {
                    onSelect(nodeId);
                    setOpen(true);
                }
            }}
        >
            <span className="tree-node-caret">
                {open ? <Ico.ChevronDown /> : <Ico.ChevronRight />}
            </span>
            <span className="tree-node-icon">{icon}</span>
            <span className="tree-node-label tree-section-label">{label}</span>
        </button>
    );

    return (
        <div className="tree-section">
            <div className="tree-section-head">
                {hasContextActions ? (
                    <ContextMenu.Root>
                        <ContextMenu.Trigger asChild>{headerButton}</ContextMenu.Trigger>
                        <ContextMenu.Portal>
                            <ContextMenu.Content className="radix-context-menu-content" alignOffset={2}>
                                {contextActions!.map((action) => (
                                    <ContextMenu.Item
                                        key={action.label}
                                        className={[
                                            "radix-context-menu-item",
                                            action.danger ? "radix-context-menu-item--danger" : "",
                                        ].join(" ").trim()}
                                        onSelect={action.onSelect}
                                        disabled={action.disabled}
                                    >
                                        {action.label}
                                    </ContextMenu.Item>
                                ))}
                            </ContextMenu.Content>
                        </ContextMenu.Portal>
                    </ContextMenu.Root>
                ) : headerButton}
            </div>
            {open && <div className="tree-section-children">{children}</div>}
        </div>
    );
}
